import {
  Payment,
  type PaymentId,
  type PaymentOpaqueValue,
  type QuoteBreakdown,
} from "@b2bportal/purchase-api";
import { type WalletOffer, DiscountEnum } from "@b2bportal/wallet-offer-api";

interface CartUpdatePaymentParams {
  addPayments: Array<PaymentOpaqueValue>;
  removePayments: Array<PaymentId>;
}

type OfferCartUpdateParams = CartUpdatePaymentParams;

/**
 * Looks up the BE-assigned payment ID for a TravelWalletOffer in the quoted
 * breakdown. The `removePayments` request must use this ID — the offer JWT
 * changes per request so it won't match the cart entry.
 *
 * Only one offer can be applied at a time, so we match by payment type only.
 */
export const findOfferPaymentId = (
  quoteBreakdown: QuoteBreakdown | undefined
): string | undefined =>
  quoteBreakdown?.payments.find(
    (p) => p.payment.type === Payment.TravelWalletOffer
  )?.id;

export const buildOfferPayment = (offerId: string): PaymentOpaqueValue => ({
  type: Payment.TravelWalletOffer,
  value: { offerId },
});

const isOfferPayment = (payment: PaymentOpaqueValue, offerId?: string) =>
  payment.type === Payment.TravelWalletOffer &&
  (offerId == null || payment.value?.offerId === offerId);

/** Queues `offerId` as an added payment; no-op if already queued. */
export const applyOfferToCartUpdateParams = <T extends OfferCartUpdateParams>(
  params: T,
  offerId: string
): T => {
  if (params.addPayments.some((p) => isOfferPayment(p, offerId))) {
    return params;
  }
  return {
    ...params,
    addPayments: [...params.addPayments, buildOfferPayment(offerId)],
  };
};

/**
 * Effective fiat discount value for comparison.
 * FiatAmount → flat amount; Percentage → maxDiscount cap (uncapped → Infinity).
 */
const getEffectiveDiscountValue = (offer: WalletOffer): number => {
  const { discount } = offer;
  switch (discount.Discount) {
    case DiscountEnum.FiatAmount:
      return Math.abs(discount.amount.fiat.value);
    case DiscountEnum.Percentage:
      return discount.maxDiscount != null
        ? Math.abs(discount.maxDiscount.fiat.value)
        : Infinity;
  }
};

/** Returns the offer with the highest effective discount, or undefined. */
export const getBestWalletOffer = (
  offers: WalletOffer[]
): WalletOffer | undefined => {
  if (offers.length === 0) return undefined;
  return offers.reduce((best, current) =>
    getEffectiveDiscountValue(current) > getEffectiveDiscountValue(best)
      ? current
      : best
  );
};

/**
 * Removes `offerId`: drops a pending add, and queues a removePayment for an
 * already-applied offer.
 */
export const removeOfferFromCartUpdateParams = <
  T extends OfferCartUpdateParams,
>(
  params: T,
  offerId: string,
  { isApplied }: { isApplied: boolean }
): T => {
  const withoutPendingAdd = params.addPayments.filter(
    (p) => !isOfferPayment(p, offerId)
  );
  const hadPendingAdd = withoutPendingAdd.length < params.addPayments.length;

  const removePayments =
    isApplied && !hadPendingAdd
      ? [
          ...params.removePayments,
          { id: offerId, payment: Payment.TravelWalletOffer },
        ]
      : params.removePayments;

  return {
    ...params,
    addPayments: withoutPendingAdd,
    removePayments,
  };
};

/**
 * Selects the offer that matches the room's pre-applied offer from hotel shop.
 * Falls back to the first offer in the list when no match is found.
 */
export const findBestOffer = (
  offers: WalletOffer[],
  roomOfferId: string | undefined
): WalletOffer => {
  if (roomOfferId != null) {
    const match = offers.find((o) => o.offerId === roomOfferId);
    if (match != null) return match;
  }
  return offers[0];
};

// --- Credit helpers ---

const isCreditPayment = (payment: PaymentOpaqueValue) =>
  payment.type === Payment.TravelWalletCredit;

export const findCreditPaymentId = (
  quoteBreakdown: QuoteBreakdown | undefined
): string | undefined =>
  quoteBreakdown?.payments.find(
    (p) => p.payment.type === Payment.TravelWalletCredit
  )?.id;

export const buildCreditPayment = (amount: {
  value: number;
  currencyCode: string;
}): PaymentOpaqueValue => ({
  type: Payment.TravelWalletCredit,
  value: {
    paymentAmount: {
      amount: amount.value,
      currency: amount.currencyCode,
    },
  },
});

/** Queues a credit payment; replaces any existing pending credit add. */
export const applyCreditToCartUpdateParams = <
  T extends CartUpdatePaymentParams,
>(
  params: T,
  amount: { value: number; currencyCode: string }
): T => ({
  ...params,
  addPayments: [
    ...params.addPayments.filter((p) => !isCreditPayment(p)),
    buildCreditPayment(amount),
  ],
});

/** Removes a credit: drops a pending add, and queues removePayment if applied. */
export const removeCreditFromCartUpdateParams = <
  T extends CartUpdatePaymentParams,
>(
  params: T,
  paymentId: string,
  { isApplied }: { isApplied: boolean }
): T => {
  const withoutPendingAdd = params.addPayments.filter(
    (p) => !isCreditPayment(p)
  );
  const hadPendingAdd = withoutPendingAdd.length < params.addPayments.length;

  const removePayments =
    isApplied && !hadPendingAdd
      ? [
          ...params.removePayments,
          { id: paymentId, payment: Payment.TravelWalletCredit },
        ]
      : params.removePayments;

  return {
    ...params,
    addPayments: withoutPendingAdd,
    removePayments,
  };
};
